import React, { useEffect, useState, useRef, useCallback } from "react";
import MaterialTable from "material-table";
import FitnessService from "../../services/FitnessService";
import { makeStyles } from "@material-ui/core/styles";
import { DateTime, Settings } from "luxon";
import {
    Chip,
    Typography,
    Box,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Divider,
} from "@material-ui/core";
import FitnessCenterIcon from "@material-ui/icons/FitnessCenter";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CloseIcon from "@material-ui/icons/Close";

Settings.defaultZone = "Europe/Sofia";

const REFRESH_INTERVAL = 10; // seconds

const useStyles = makeStyles((theme) => ({
    toolbar: {
        marginLeft: theme.spacing(2),
    },
    headerBox: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(2),
        backgroundColor: "#f5f5f5",
        borderBottom: "1px solid #e0e0e0",
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
    },
    headerIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    entryCountChip: {
        fontWeight: "bold",
    },
    refreshContainer: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    countdownBox: {
        display: "flex",
        alignItems: "center",
        backgroundColor: "#e3f2fd",
        borderRadius: theme.spacing(1),
        padding: theme.spacing(0.5, 1.5),
        minWidth: 180,
    },
    countdownText: {
        fontSize: "0.85rem",
        color: theme.palette.primary.main,
        marginRight: theme.spacing(1),
    },
    countdownNumber: {
        fontWeight: "bold",
        fontSize: "1rem",
        color: theme.palette.primary.dark,
        minWidth: 24,
        textAlign: "center",
    },
    progressBar: {
        width: 60,
        marginLeft: theme.spacing(1),
        borderRadius: 4,
    },
    refreshIcon: {
        animation: "$spin 1s linear infinite",
    },
    "@keyframes spin": {
        "0%": {
            transform: "rotate(0deg)",
        },
        "100%": {
            transform: "rotate(360deg)",
        },
    },
    dialogTitle: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: theme.palette.primary.main,
        color: "white",
    },
    dialogContent: {
        padding: theme.spacing(2),
        minWidth: 400,
    },
    entryListItem: {
        borderBottom: "1px solid #f0f0f0",
    },
    guestInfo: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: "#f9f9f9",
        borderRadius: theme.spacing(1),
    },
}));

const FitnessTable = () => {
    const classes = useStyles();
    const tableRef = useRef(null);
    const refreshIntervalRef = useRef(null);
    const countdownIntervalRef = useRef(null);

    const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // State for entry details dialog
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedGuest, setSelectedGuest] = useState(null);
    const [guestEntries, setGuestEntries] = useState([]);
    const [loadingEntries, setLoadingEntries] = useState(false);

    const [iserror, setIserror] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    // Function to refresh data
    const refreshData = useCallback(() => {
        if (tableRef.current) {
            setIsRefreshing(true);
            tableRef.current.onQueryChange();
            // Reset refreshing state after a short delay
            setTimeout(() => setIsRefreshing(false), 500);
        }
        setCountdown(REFRESH_INTERVAL);
    }, []);

    // Manual refresh handler
    const handleManualRefresh = useCallback(() => {
        refreshData();
    }, [refreshData]);

    // Setup auto-refresh every 30 seconds with countdown
    useEffect(() => {
        // Countdown timer - updates every second
        countdownIntervalRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    return REFRESH_INTERVAL;
                }
                return prev - 1;
            });
        }, 1000);

        // Refresh timer - refreshes data every 30 seconds
        refreshIntervalRef.current = setInterval(() => {
            refreshData();
        }, REFRESH_INTERVAL * 1000);

        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
        };
    }, [refreshData]);

    const formatDateTime = (dateString) => {
        if (!dateString) return "-";
        try {
            return DateTime.fromISO(dateString)
                .setZone("Europe/Sofia")
                .toFormat("dd.MM.yyyy HH:mm");
        } catch (e) {
            return dateString;
        }
    };

    // Handle viewing entry details for a guest
    const handleViewEntries = async (guest) => {
        setSelectedGuest(guest);
        setDetailsOpen(true);
        setLoadingEntries(true);

        try {
            // Fetch all entries for this guest from the entries endpoint
            const response = await FitnessService.getEntries(0, 1000, guest.guest_name, "time", "DESC");
            // Filter to only show entries for this specific user
            const userEntries = response.data.data.filter(
                (entry) => entry.user_id === guest.user_id || entry.sap === guest.user_id
            );
            setGuestEntries(userEntries);
        } catch (error) {
            console.error("Error fetching guest entries:", error);
            setGuestEntries([]);
        } finally {
            setLoadingEntries(false);
        }
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedGuest(null);
        setGuestEntries([]);
    };

    const [columns] = useState([
        {
            title: "Име на Гост",
            field: "guest_name",
            editable: "never",
            cellStyle: (e, rowData) => {
                if (rowData.guest_name === "null" || rowData.guest_name === "empty") {
                    return { backgroundColor: "#FFCCCC" };
                }
            },
            emptyValue: () => "Неизвестен",
        },
        {
            title: "Последно посещение",
            field: "last_entry",
            editable: "never",
            render: (rowData) => formatDateTime(rowData.last_entry),
            cellStyle: {
                textAlign: "center",
            },
            headerStyle: {
                textAlign: "center",
            },
        },
        {
            title: "Брой посещения",
            field: "entry_count",
            editable: "never",
            type: "numeric",
            render: (rowData) => (
                <Chip
                    label={rowData.entry_count || 0}
                    color="primary"
                    size="small"
                    className={classes.entryCountChip}
                />
            ),
            cellStyle: {
                textAlign: "center",
            },
            headerStyle: {
                textAlign: "center",
            },
        },
        {
            title: "ID",
            field: "user_id",
            editable: "never",
            cellStyle: { width: "10%" },
            width: "10%",
            headerStyle: { width: "10%" },
            emptyValue: () => "empty",
        },
    ]);

    // Calculate progress percentage (inverted - starts full, goes to empty)
    const progressValue = (countdown / REFRESH_INTERVAL) * 100;

    return (
        <div>
            <Box className={classes.headerBox}>
                <Box className={classes.headerLeft}>
                    <FitnessCenterIcon className={classes.headerIcon} fontSize="large" />
                    <Typography variant="h6">Фитнес - Посещения на гости</Typography>
                </Box>
                <Box className={classes.refreshContainer}>
                    <Box className={classes.countdownBox}>
                        {isRefreshing ? (
                            <RefreshIcon className={classes.refreshIcon} color="primary" />
                        ) : (
                            <>
                                <Typography className={classes.countdownText}>
                                    Обновяване след:
                                </Typography>
                                <Typography className={classes.countdownNumber}>
                                    {countdown}сек.
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={progressValue}
                                    className={classes.progressBar}
                                />
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
            <MaterialTable
                title=""
                columns={columns}
                tableRef={tableRef}
                data={(query) =>
                    new Promise((resolve, reject) => {
                        // Use the summary endpoint which groups by user
                        FitnessService.getSummary(
                            query.page,
                            query.pageSize,
                            query.search
                        )
                            .then((response) => {
                                resolve({
                                    data: response.data.data,
                                    page: response.data.page,
                                    totalCount: response.data.totalCount,
                                });
                            })
                            .catch((error) => {
                                console.error("Error fetching fitness entries:", error);
                                setErrorMessages(["Грешка при зареждане на данните"]);
                                setIserror(true);
                                reject(error);
                            });
                    })
                }
                style={{
                    borderRadius: "0",
                    boxShadow: "none",
                }}
                actions={[
                    {
                        icon: "refresh",
                        tooltip: "Обнови сега",
                        isFreeAction: true,
                        onClick: handleManualRefresh,
                    },
                    {
                        icon: () => <VisibilityIcon />,
                        tooltip: "Виж всички посещения",
                        onClick: (event, rowData) => handleViewEntries(rowData),
                    },
                ]}
                options={{
                    loadingType: "linear",
                    pageSize: 50,
                    maxBodyHeight: "68vh",
                    showTitle: false,
                    sorting: true,
                    actionsColumnIndex: -1,
                    exportButton: { csv: true, pdf: false },
                    filtering: false,
                    pageSizeOptions: [50, 100, 200],
                    search: true,
                    toolbar: true,
                    debounceInterval: 500,
                    searchFieldAlignment: "right",
                    searchFieldStyle: { width: "300px" },
                }}
                localization={{
                    pagination: {
                        labelDisplayedRows: "{from}-{to} от {count}",
                        labelRowsSelect: "Гости",
                    },
                    header: {
                        actions: "Действия",
                    },
                    body: {
                        emptyDataSourceMessage: "Няма записи за фитнес посещения",
                        filterRow: {
                            filterTooltip: "Филтър",
                        },
                    },
                    toolbar: {
                        exportTitle: "Експорт",
                        exportName: "Експорт към CSV",
                        searchTooltip: "Търси",
                        searchPlaceholder: "Търси по име...",
                    },
                }}
            />

            {/* Entry Details Dialog */}
            <Dialog
                open={detailsOpen}
                onClose={handleCloseDetails}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle disableTypography className={classes.dialogTitle}>
                    <Typography variant="h6">
                        Всички посещения
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDetails}
                        style={{ color: "white" }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {selectedGuest && (
                        <Box className={classes.guestInfo}>
                            <Typography variant="subtitle1">
                                <strong>Гост:</strong> {selectedGuest.guest_name}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Общо посещения:</strong> {selectedGuest.entry_count}
                            </Typography>
                        </Box>
                    )}
                    <Divider />
                    <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                        История на посещенията:
                    </Typography>
                    {loadingEntries ? (
                        <Typography>Зареждане...</Typography>
                    ) : guestEntries.length > 0 ? (
                        <List dense>
                            {guestEntries.map((entry, index) => (
                                <ListItem key={entry.id || index} className={classes.entryListItem}>
                                    <ListItemIcon>
                                        <AccessTimeIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={formatDateTime(entry.time)}
                                        secondary={entry.event === "enter" ? "Влизане" : entry.event === "exit" ? "Излизане" : "Достъп до фитнес"}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography color="textSecondary">
                            Няма записани посещения
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetails} color="primary">
                        Затвори
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default FitnessTable;
